import React from 'react';
import { monthlyPriceFor, setupFeeFor } from '../config/pricing';

function PricingModal({ isOpen, onClose, currentTier = 'free_trial' }) {
  if (!isOpen) return null;

  // Prices come from config/pricing.js. This modal previously advertised
  // Starter at $299 setup + $49/mo, Pro at $799 setup and Enterprise at $1999
  // setup -- none of which matched what the platform actually charges.
  const tiers = [
    {
      id: 'free_trial',
      name: 'Free Trial',
      setupFee: setupFeeFor('free_trial'),
      monthlyFee: monthlyPriceFor('free_trial'),
      limits: {
        activeLoans: 3,
        states: 1,
        properties: 10
      },
      features: [],
      buttonText: 'Current Plan',
      buttonDisabled: true
    },
    {
      id: 'starter',
      name: 'Starter',
      setupFee: setupFeeFor('starter'),
      monthlyFee: monthlyPriceFor('starter'),
      limits: {
        activeLoans: 25,
        states: 2,
        properties: 50
      },
      features: [
        'Payment processing',
        'Logo upload',
        'Custom domain',
        'Backup/Export'
      ],
      buttonText: 'Upgrade',
      buttonDisabled: false
    },
    {
      id: 'pro',
      name: 'Pro',
      setupFee: setupFeeFor('pro'),
      monthlyFee: monthlyPriceFor('pro'),
      popular: true,
      limits: {
        activeLoans: 100,
        states: 5,
        properties: 200
      },
      features: [
        'Everything in Starter PLUS:',
        'eBay Integration',
        'Tax Escrow',
        'Auction Calendar',
        'Multi-user (3 seats)',
        'SMS Notifications'
      ],
      buttonText: 'Upgrade',
      buttonDisabled: false
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      setupFee: setupFeeFor('enterprise'),
      monthlyFee: monthlyPriceFor('enterprise'),
      limits: {
        activeLoans: '∞',
        states: '∞',
        properties: '∞'
      },
      features: [
        'Everything in Pro PLUS:',
        'Bulk Import',
        'API Access',
        'Custom Email Templates',
        'No DeedStack branding'
      ],
      buttonText: 'Contact Sales',
      buttonDisabled: false
    }
  ];

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleUpgrade = (tierId) => {
    if (tierId === 'enterprise') {
      // For enterprise, open contact page
      window.open('https://deedstack.com/contact', '_blank');
    } else {
      // TODO: Wire up payment processing
      console.log('Upgrade to:', tierId);
      alert(`Upgrade to ${tierId} - Payment integration coming soon!`);
    }
  };

  return (
    <div className="pricing-modal-overlay" onClick={handleOverlayClick}>
      <div className="pricing-modal-container">
        <div className="pricing-modal-header">
          <h2>Upgrade Your Plan</h2>
          <button className="pricing-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="pricing-modal-body">
          <div className="pricing-tiers-grid">
            {tiers.map((tier) => {
              const isCurrent = currentTier === tier.id;

              return (
                <div
                  key={tier.id}
                  className={`pricing-tier-card ${tier.popular ? 'pricing-tier-popular' : ''} ${isCurrent ? 'pricing-tier-current' : ''}`}
                >
                  {tier.popular && (
                    <div className="pricing-popular-badge">Most Popular</div>
                  )}

                  <h3 className="pricing-tier-name">{tier.name}</h3>

                  <div className="pricing-tier-price">
                    {tier.setupFee === 0 ? (
                      <span className="pricing-amount">$0</span>
                    ) : (
                      <>
                        <span className="pricing-amount">${tier.setupFee.toLocaleString()}</span>
                        <span className="pricing-setup">setup</span>
                      </>
                    )}
                  </div>

                  {tier.monthlyFee > 0 && (
                    <div className="pricing-monthly">
                      + ${tier.monthlyFee}/mo
                    </div>
                  )}

                  <div className="pricing-limits">
                    <div className="pricing-limit-item">
                      <span className="pricing-limit-value">{tier.limits.activeLoans}</span>
                      <span className="pricing-limit-label">Active Loans</span>
                    </div>
                    <div className="pricing-limit-item">
                      <span className="pricing-limit-value">{tier.limits.states}</span>
                      <span className="pricing-limit-label">{tier.limits.states === 1 ? 'State' : 'States'}</span>
                    </div>
                    <div className="pricing-limit-item">
                      <span className="pricing-limit-value">{tier.limits.properties}</span>
                      <span className="pricing-limit-label">Properties</span>
                    </div>
                  </div>

                  {tier.features.length > 0 && (
                    <ul className="pricing-features">
                      {tier.features.map((feature, index) => (
                        <li key={index} className={feature.includes('PLUS:') ? 'pricing-feature-header' : ''}>
                          {!feature.includes('PLUS:') && <span className="pricing-check">&#10003;</span>}
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="pricing-tier-action">
                    {isCurrent ? (
                      <button className="btn pricing-btn-current" disabled>
                        Current Plan
                      </button>
                    ) : (
                      <button
                        className={`btn ${tier.popular ? 'pricing-btn-popular' : 'btn-primary'}`}
                        onClick={() => handleUpgrade(tier.id)}
                      >
                        {tier.buttonText}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="pricing-modal-footer">
          <p>All plans include secure hosting, automatic updates, and email support.</p>
          <p>Questions? <a href="https://deedstack.com/contact" target="_blank" rel="noopener noreferrer">Contact us</a></p>
        </div>
      </div>
    </div>
  );
}

export default PricingModal;
